import React, { Suspense, useState, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import LandingPageSheetSkeleton from '@app/views/LandingPage/ui/LandingPageSheetSkeleton';
import { useIsLoadVpcSheetVisible, useIsOpenDesignSheetOpen } from '@app/stores/designStore';

const LandingPageSheets = React.lazy(() => import('@app/views/LandingPage/ui/LandingPageSheets'));

// Overlay that fades out to reveal the real sheet underneath
// Only covers screen when a sheet is actually visible
const SkeletonOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  pointer-events: ${props => (props.$isVisible ? 'auto' : 'none')};
  visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
`;

interface LandingPageSheetsWithTransitionProps {
  enableSaveToProfile?: boolean;
}

/**
 * Wrapper component that provides smooth transition from skeleton to real sheet.
 *
 * Flow:
 * 1. First open: Show skeleton while lazy loading component
 * 2. Once component mounts, it stays in memory
 * 3. Subsequent opens: Component already loaded, no skeleton needed
 * 4. Result: Skeleton only on first open, instant on subsequent opens
 */
const LandingPageSheetsWithTransition: React.FC<LandingPageSheetsWithTransitionProps> = ({
  enableSaveToProfile
}) => {
  const [hasEverMounted, setHasEverMounted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  // Check if any sheet is visible
  const loadVpcSheetVisible = useIsLoadVpcSheetVisible();
  const openDesignSheetVisible = useIsOpenDesignSheetOpen();
  const isAnySheetVisible = enableSaveToProfile ? openDesignSheetVisible : loadVpcSheetVisible;

  // Show skeleton only if component hasn't mounted yet
  useEffect(() => {
    if (isAnySheetVisible && !hasEverMounted) {
      setIsLoaded(false);
      setShowSkeleton(true);
    }
  }, [isAnySheetVisible, hasEverMounted]);

  // Handle skeleton removal when loaded
  useEffect(() => {
    if (isLoaded) {
      // Mark as mounted - skeleton will never show again
      setHasEverMounted(true);

      if (showSkeleton) {
        // Fade out skeleton then remove from DOM
        const timer = setTimeout(() => {
          setShowSkeleton(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoaded, showSkeleton]);

  // Reset isLoaded when sheet closes (but keep hasEverMounted)
  useEffect(() => {
    if (!isAnySheetVisible) {
      setIsLoaded(false);
      setShowSkeleton(false);
    }
  }, [isAnySheetVisible]);

  return (
    <>
      {/* Real sheet - loads underneath skeleton */}
      <Suspense fallback={null}>
        <LoadedTracker onLoad={() => setIsLoaded(true)}>
          <LandingPageSheets enableSaveToProfile={enableSaveToProfile} />
        </LoadedTracker>
      </Suspense>

      {/* Skeleton overlay - removed from DOM after fade-out completes */}
      {showSkeleton && (
        <SkeletonOverlay $isVisible={!isLoaded}>
          <LandingPageSheetSkeleton enableSaveToProfile={enableSaveToProfile} />
        </SkeletonOverlay>
      )}
    </>
  );
};

// Helper component that notifies parent when child has mounted
const LoadedTracker: React.FC<{ onLoad: () => void; children: React.ReactNode }> = ({
  onLoad,
  children
}) => {
  useLayoutEffect(() => {
    // Call onLoad immediately after DOM updates
    onLoad();
  }, [onLoad]);

  return <>{children}</>;
};

export default LandingPageSheetsWithTransition;
